import {PageHeader} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
  GUEST_ACCOUNT_FILTER_FIELDS,
  GUEST_ACCOUNT_SORT_CONFIG,
  GuestAccountsList,
  GuestModeBanner,
  useGuestAccountsQuery
} from '@/features/guest';
import {
  TrustAccountListEmptySearch,
  TrustAccountListToolbar
} from '@/features/trust-accounts/components';
import {useTrustAccountListToolbar} from '@/features/trust-accounts/hooks';
import {PageContent, PageLayout} from '@/layouts';

import type {FC} from 'react';

const TOOLBAR_OPTIONS = {
  sortConfig: GUEST_ACCOUNT_SORT_CONFIG,
  filterFields: GUEST_ACCOUNT_FILTER_FIELDS,
  viewKey: 'guest:accounts:view',
  defaultView: 'card'
} as const;

export const GuestTrustAccountsPage: FC = () => {
  const {t} = useTranslation('guest');
  const {data: accounts, error, refetch} = useGuestAccountsQuery();
  const {toolbar, visibleAccounts, clearSearch, clearFilters} = useTrustAccountListToolbar(
    accounts,
    TOOLBAR_OPTIONS
  );
  const hasAccounts = Boolean(accounts?.length);
  const firmCount = useMemo(
    () => new Set(accounts?.map(account => account.firm.id)).size,
    [accounts]
  );

  return (
    <PageLayout error={error} refetch={() => void refetch()}>
      <PageHeader
        title={t('accounts.title')}
        description={t('accounts.description')}
        badge={
          hasAccounts
            ? t('accounts.counts', {
                first: t('accounts.accountCount', {count: accounts?.length}),
                second: t('accounts.firmCount', {count: firmCount})
              })
            : undefined
        }
      />
      <PageContent className="flex flex-col gap-4">
        {(!accounts || hasAccounts) && (
          <TrustAccountListToolbar {...toolbar} dateLabel={t('accounts.row.sharedOn')} />
        )}
        {hasAccounts && <GuestModeBanner />}
        {hasAccounts && visibleAccounts?.length === 0 ? (
          <TrustAccountListEmptySearch
            searchText={toolbar.searchText}
            onClearSearch={clearSearch}
            onClearFilters={clearFilters}
          />
        ) : (
          <GuestAccountsList
            accounts={visibleAccounts}
            view={toolbar.view}
            sorting={toolbar.sorting}
            onSortingChange={toolbar.onSortingChange}
          />
        )}
      </PageContent>
    </PageLayout>
  );
};
